const STORAGE_KEY = 'iland.auth.tokens';

export interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  provider?: string;
}

export interface IdTokenClaims {
  sub: string;
  email: string;
  name?: string;
  exp: number;
  'cognito:username': string;
  identities?: unknown;
}

export const decodeIdToken = (idToken: string): IdTokenClaims | null => {
  const payload = idToken.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as Partial<IdTokenClaims>;
    if (!claims.sub || !claims.exp || !claims['cognito:username']) {
      return null;
    }
    return {
      sub: claims.sub,
      email: claims.email || '',
      name: claims.name,
      exp: claims.exp,
      'cognito:username': claims['cognito:username'],
      identities: claims.identities,
    };
  } catch {
    return null;
  }
};

export const loadTokens = (): StoredTokens | null => {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (!parsed.idToken || !parsed.accessToken || !parsed.refreshToken) {
      return null;
    }
    return {
      idToken: parsed.idToken,
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      provider: typeof parsed.provider === 'string' ? parsed.provider : undefined,
    };
  } catch {
    return null;
  }
};

export const saveTokens = (tokens: StoredTokens): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
};

export const clearTokens = (): void => {
  window.localStorage.removeItem(STORAGE_KEY);
};

// A minute of head room so a token cannot expire mid-request.
export const isExpired = (claims: IdTokenClaims): boolean =>
  claims.exp * 1000 - 60_000 <= Date.now();
